import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Alert,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  PhotoCamera,
  Delete,
  CloudUpload,
  Cancel,
  Business
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { uploadOrganizationLogo, deleteOrganizationLogo, validateLogoFile } from '../services/logoService';

export default function OrganizationLogoManager({ organization, onLogoUpdate }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  // Current logo from organization
  const currentLogo = organization?.logo?.url;

  useEffect(() => {
    // Cleanup preview URL when component unmounts or file changes
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Drag and drop event handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (uploading) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      handleFileValidation(file);
    }
  };

  const handleFileValidation = (file) => {
    setError('');

    if (!file) {
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    try {
      validateLogoFile(file);
      setSelectedFile(file);
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } catch (err) {
      setError(err.message);
      setSelectedFile(null);
      setPreviewUrl(null);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    handleFileValidation(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      const result = await uploadOrganizationLogo(selectedFile);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      // Cleanup
      setSelectedFile(null);
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Notify parent component
      if (onLogoUpdate) {
        onLogoUpdate(result.logo);
      }

      enqueueSnackbar('Logo uploaded successfully!', { variant: 'success' });
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError(err.response?.data?.error || 'Failed to upload logo');
      enqueueSnackbar('Failed to upload logo', { variant: 'error' });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError('');

    try {
      await deleteOrganizationLogo();
      
      // Notify parent component
      if (onLogoUpdate) {
        onLogoUpdate(null);
      }

      setDeleteDialogOpen(false);
      enqueueSnackbar('Logo deleted successfully!', { variant: 'success' });
    } catch (err) {
      console.error('Error deleting logo:', err);
      setError(err.response?.data?.error || 'Failed to delete logo');
      enqueueSnackbar('Failed to delete logo', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileInputClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Organization Logo
        </Typography>
        
        <Box
          ref={dropZoneRef}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          sx={{
            position: 'relative',
            border: '2px dashed',
            borderColor: uploading ? 'grey.400' : (dragActive ? 'primary.main' : 'divider'),
            borderRadius: 2,
            backgroundColor: uploading ? 'grey.50' : (dragActive ? 'primary.50' : 'transparent'),
            transition: 'all 0.2s ease',
            p: 3,
            opacity: uploading ? 0.6 : 1,
            pointerEvents: uploading ? 'none' : 'auto',
            '&:hover': {
              borderColor: uploading ? 'grey.400' : 'primary.main',
              backgroundColor: uploading ? 'grey.50' : 'primary.50'
            }
          }}
        >
          {/* Drag overlay */}
          {dragActive && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                border: '2px dashed',
                borderColor: 'primary.main',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
                pointerEvents: 'none'
              }}
            >
              <Typography variant="h6" color="primary.main" sx={{ textAlign: 'center' }}>
                Drop your logo here
              </Typography>
            </Box>
          )}

          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            {/* Current Logo Display */}
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                src={previewUrl || currentLogo}
                sx={{ 
                  width: 120, 
                  height: 120,
                  border: '2px solid',
                  borderColor: 'divider'
                }}
              >
                <Business sx={{ fontSize: 60 }} />
              </Avatar>
              
              {currentLogo && !selectedFile && (
                <Tooltip title="Delete current logo">
                  <IconButton
                    color="error"
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={deleting}
                  >
                    {deleting ? <CircularProgress size={24} /> : <Delete />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          )}

          {/* File Selection */}
          {!selectedFile && (
            <>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/jpeg,image/jpg,image/png,image/webp"
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                startIcon={<PhotoCamera />}
                onClick={handleFileInputClick}
                disabled={uploading}
                sx={{ 
                  mt: 1,
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  py: 1.5,
                  px: 3
                }}
              >
                {currentLogo ? 'Change Logo' : 'Click to Upload Logo'}
              </Button>
            </>
          )}

          {/* Selected File Actions */}
          {selectedFile && (
            <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center">
              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={handleUpload}
                disabled={uploading}
                color="primary"
              >
                {uploading ? 'Uploading...' : 'Upload Logo'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={handleCancel}
                disabled={uploading}
              >
                Cancel
              </Button>
            </Box>
          )}

          {/* Upload Progress */}
          {uploading && (
            <Box width="100%">
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress} 
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                Uploading... {uploadProgress}%
              </Typography>
            </Box>
          )}

          {/* File Info */}
          {selectedFile && (
            <Typography variant="body2" color="text.secondary" align="center">
              Selected: {selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
            </Typography>
          )}

            {/* Instructions */}
            <Typography variant="body2" color="text.secondary" align="center">
              <strong>Drag & drop an image here</strong> or click to browse
              <br />
              Supported formats: JPG, PNG, WebP (max 5MB)
              <br />
              Recommended size: 200x200 pixels or larger
            </Typography>
          </Box>
        </Box>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm">
        <DialogTitle>Delete Organization Logo</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the current organization logo? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
          >
            {deleting ? 'Deleting...' : 'Delete Logo'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
} 